import { useDispatch } from 'react-redux';
import { useFormContext } from 'react-hook-form';
import { useMutation } from '@apollo/client';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { upsertFrequencyManagementSettings } from 'modules/frequencyManagement/lib/queries/upsertFrequencyManagementSettings';
import { FrequencyManagementSchema } from '../contexts/FormProvider';
import { SupportedMediums } from 'modules/frequencyManagement/lib/types';
import { createPayload, createUnsavedFormMessageTypeModel } from './useOnSubmit.utils';
import { createDefaultValues } from '../contexts/FormProvider.defaultValues';
import { useEditStateContext } from '../contexts/EditStateContext';

export const useOnSubmit = (messageMedium: SupportedMediums) => {
  const dispatch = useDispatch();
  const {
    reset,
    handleSubmit,
    formState: { dirtyFields },
  } = useFormContext<FrequencyManagementSchema>();
  const { clearEditState } = useEditStateContext();

  const [upsert, { loading, error }] = useMutation<any>(upsertFrequencyManagementSettings, {
    onCompleted: (response) => {
      const { success, failure } = response?.upsertFrequencyManagementSettings;

      if (failure || !success) {
        const message =
          failure?.message ||
          'Oops! Frequency management data could not be saved. Please try again.';
        dispatch(
          addNegativeToastWithErrorId(
            message,
            'upsertFrequencyManagementSettings',
            failure?.message || 'No failure message',
          ),
        );
      } else {
        const mediumConfig = success[messageMedium];
        const defaultValues = createDefaultValues(mediumConfig);
        dispatch(addPositiveToast('Success! Your frequency management settings are saved.'));
        reset(defaultValues);
        clearEditState();
      }
    },
    onError: (err) => {
      dispatch(
        addNegativeToastWithErrorId(
          'Oops! Frequency management data could not be saved. Please try again.',
          'upsertFrequencyManagementSettings',
          err,
        ),
      );
    },
    refetchQueries: ['fetchFrequencyManagementSettings'],
  });

  const onSubmit = (values: FrequencyManagementSchema) => {
    const current = createUnsavedFormMessageTypeModel(dirtyFields, values);
    const settings = createPayload(messageMedium, current);

    upsert({
      variables: { settings },
    });
  };

  return { onSubmit: handleSubmit(onSubmit), loading, error };
};
